// The one fetch wrapper. Every /api and /portal/api call goes through here so that:
//  - a 401 sends the browser to sign-in: staff to /auth/login (Microsoft), customers to /portal/saml/login
//    (Sentinel IdP), each with returnTo=<current path> (served by the API, not React);
//  - a non-2xx body of { error } becomes an ApiRequestError with that message;
//  - a 204 resolves to null;
//  - in demo mode (?demo in the URL) requests are answered from src/demo.ts instead of the network.
import type { ApiError } from "@kajeet-service/shared";

export class ApiRequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

const DEMO_KEY = "kajeet-service:demo";

function readDemoFlag(): boolean {
  // Real data unless ?demo is in this URL: demo mode is never remembered across loads.
  const params = new URLSearchParams(window.location.search);
  try {
    sessionStorage.removeItem(DEMO_KEY);
  } catch {}
  return params.has("demo") && params.get("demo") !== "0" && params.get("demo") !== "off";
}

/** Decided once at load: only an explicit `?demo` in the URL shows demo data. */
export const DEMO = readDemoFlag();

/** `?demo&guest`: the portal as a signed-link guest. Also decided at load, so in-app navigation keeps it. */
export const DEMO_GUEST = DEMO && new URLSearchParams(window.location.search).has("guest");

/** `?demo&impersonate`: the portal as staff viewing it as the demo customer (the tab "View portal as" opens). */
export const DEMO_IMPERSONATING = DEMO && new URLSearchParams(window.location.search).has("impersonate");

export function loginUrl(portal = false): string {
  const here = window.location.pathname + window.location.search;
  return `${portal ? "/portal/saml/login" : "/auth/login"}?returnTo=${encodeURIComponent(here)}`;
}

/**
 * Portal pages that have a public version: signed out, the portal home, help articles and "open a
 * case" go there (the public contact form and help, each with a "Sign in" link) instead of to the
 * Sentinel sign-in. The server does the same before the page loads (portal/public.ts publicPortalPath).
 */
export function publicPortalPage(path = window.location.pathname): string | null {
  if (/^\/portal(?:\/new)?\/?$/.test(path)) return "/portal/contact";
  if (/^\/portal\/kb\/?$/.test(path)) return "/portal/help";
  const topic = /^\/portal\/kb\/topics\/([^/]+)\/?$/.exec(path);
  if (topic) return `/portal/help/topics/${topic[1]}`;
  const article = /^\/portal\/kb\/([^/]+)\/?$/.exec(path);
  return article ? `/portal/help/${article[1]}` : null;
}

let redirecting = false;

/** sessionStorage key: this tab is staff viewing the portal as a customer (PortalLayout.tsx). */
export const IMPERSONATING_KEY = "kn-impersonating";

function sendToLogin(portal: boolean): Promise<never> {
  if (!redirecting) {
    redirecting = true;
    // A staff "view as customer" session that ended (the hour, desk sign-out): back to the desk,
    // never the customer's Sentinel sign-in.
    let impersonating = false;
    try {
      impersonating = portal && !!sessionStorage.getItem(IMPERSONATING_KEY);
      if (impersonating) sessionStorage.removeItem(IMPERSONATING_KEY);
    } catch {}
    window.location.assign(impersonating ? "/desk" : ((portal ? publicPortalPage() : null) ?? loginUrl(portal)));
  }
  // Never settle: the page is navigating away, so don't flash an error state.
  return new Promise<never>(() => {});
}

async function errorMessage(res: Response): Promise<string> {
  const fallback = `${res.status} ${res.statusText || "request failed"}`.trim();
  try {
    const text = await res.text();
    if (!text) return fallback;
    const body = JSON.parse(text) as Partial<ApiError>;
    return typeof body.error === "string" && body.error ? body.error : fallback;
  } catch {
    return fallback;
  }
}

type Json = Record<string, unknown> | unknown[];

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: Json;
  /** multipart upload (attachments); mutually exclusive with body */
  form?: FormData;
  signal?: AbortSignal;
};

/** Resolves to the parsed JSON body, or null for 204 No Content. */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T | null> {
  if (DEMO) {
    const { demoRequest } = await import("./demo");
    return demoRequest<T>(path, opts);
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(opts.body ? { "Content-Type": "application/json" } : {}) },
    signal: opts.signal,
  };
  if (opts.body) init.body = JSON.stringify(opts.body);
  else if (opts.form) init.body = opts.form;
  // Staff viewing as a customer: say which session this tab shows, so a tab left over from an earlier
  // "view as" can't act as the contact viewed since (identity/customer.ts). /me always answers.
  let shown: string | null = null;
  try {
    shown = path.startsWith("/portal/api/") && path !== "/portal/api/me" ? sessionStorage.getItem(IMPERSONATING_KEY) : null;
  } catch {}
  if (shown) init.headers = { ...init.headers, "x-kn-as": shown };

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiRequestError(0, "Can't reach the support service. Check your connection and try again.");
  }
  if (res.status === 401) return sendToLogin(path.startsWith("/portal/"));
  if (res.status === 409 && res.headers.get("x-kn-stale")) {
    // Out of date (see above): reload, which shows whoever the portal is viewing as now.
    try {
      sessionStorage.removeItem(IMPERSONATING_KEY);
    } catch {}
    window.location.reload();
    return new Promise<never>(() => {});
  }
  if (!res.ok) throw new ApiRequestError(res.status, await errorMessage(res));
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : null;
}

/** GET that must return a body; a 204 here is a contract error. */
export async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const data = await request<T>(path, { signal });
  if (data === null) throw new ApiRequestError(204, `Empty response from ${path}`);
  return data;
}

export function post(path: string, body: Json): Promise<unknown> {
  return request<unknown>(path, { method: "POST", body });
}

/** POST/PUT that returns a JSON body (the updated record). */
export async function send<T>(path: string, body: Json, method: "POST" | "PUT" | "PATCH" = "POST"): Promise<T> {
  const data = await request<T>(path, { method, body });
  return data as T;
}

/** Multipart upload of one file under the field name "file". */
export async function upload<T>(path: string, file: File, extra: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  form.set("file", file, file.name);
  for (const [k, v] of Object.entries(extra)) form.set(k, v);
  return (await request<T>(path, { method: "POST", form })) as T;
}

/** Lists may come back bare or wrapped in { items }; accept both. */
export function itemsOf<T>(v: T[] | { items: T[] } | null | undefined): T[] {
  if (!v) return [];
  return Array.isArray(v) ? v : (v.items ?? []);
}

/** Builds "/api/x?a=1&b=2", dropping empty values. */
export function withQuery(path: string, params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}
